import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  CardContent,
  Grid,
  Backdrop,
  TextField,
  Tooltip,
  Button,
} from "@material-ui/core";
import moment from "moment";
import "moment-timezone";
import TabHeader from "src/components/TabHeader/TabHeader";
import { useStyles } from "./style";
import { useFormik } from "formik";
import { GridRowModel } from "@material-ui/x-grid";
import CommonButton from "src/components/CommonButton/CommonButton";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { exportColumns, importColumns } from "./components/columns";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { consignmentTypeList } from "src/common/utils/app.dropdowns";
import { Info } from "@material-ui/icons";
import CommonDialog from "src/components/AlertDialog/CommonDialog";
import {
  getInfoColumns,
  getInfoRows,
  getLabel,
  addSerialNumber,
  transformKey,
  validationSchema,
  gridMaxHeightHandler,
  getColumn,
  processTypeName,
} from "./components/utils";
import { exportTransformColumn } from "./components/exportTransformColumn";
import { importTransformColumn } from "./components/importTransformColumn";
import CommonLoader from "src/components/Loader/CommonLoader";

interface Props {}

export function WebocData({}: Props): React.ReactElement {
  const classes = useStyles();
  const moduleName = "WeBOC GD Status";
  const pageHeading = "WeBOC GD Status";
  const [impDisplayColumns, setImpDisplayColumns] = React.useState<any>(importColumns);
  const [expDisplayColumns, setExpDisplayColumns] = React.useState<any>(exportColumns);
  const [fileProcessTypeColumns, setFileProcessTypeColumns] = React.useState<any>([]);
  const [fileProcessTypeList, setFileProcessTypeList] = React.useState<any>([]);
  const [gridRecord, setGridRecord] = React.useState<GridRowModel[]>([]);
  const [webokProcess, setWebokProcess] = React.useState<GridRowModel[]>([]);
  const [loader, setLoader] = useState<any>(false);
  const [pageLoader, setPageLoader] = useState<any>(false);
  const [isOpenInfoDailog, setIsOpenInfoDailog] = useState<boolean>(false);
  const [isOpenFileDailog, setIsOpenFileDailog] = useState<boolean>(false);
  const { gridMaxHeight, dppr } = gridMaxHeightHandler();

  const formik = useFormik({
    initialValues: {
      processType: consignmentTypeList[0].name,
      gdNumber: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const { processType, gdNumber } = values;

      const body = {
        processType: processType,
        IdNumber: gdNumber,
      };
      // resetSearch();
      setGridRecord([]);
      setLoader(true);
      try {
        const results = await fetchWrapper.post("get_weboc_details", body);

        if (processType == processTypeName.import) {
          const transformedRows: any = await addSerialNumber(
            results?.value?.getWebocImportOpenTruckDetailModel
          );
          setGridRecord(transformedRows || []);
          setLoader(false);

          return;
        }
        if (processType == processTypeName.export) {
          const transformedRows: any = await addSerialNumber(
            results?.value?.getWebocExportOpenTruckDetailModel
          );
          setGridRecord(transformedRows || []);
          setLoader(false);

          return;
        }
      } catch (error) {
        setLoader(false);
      }
      setLoader(false);
    },
  });

  const getProcessTypeList = async () => {
    setPageLoader(true);
    try {
      const results = await fetchWrapper.get("get_weboc_processes");
      setWebokProcess(results?.value?.webocProcessModel);
      formik.setFieldValue("processType", results?.value?.webocProcessModel[0].name);
    } catch (error) {
      setPageLoader(false);
    }
    setPageLoader(false);
  };

  const getWeBOCFileDetails = async (
    gatePassId?: number,
    vehicleNumber?: string,
    messageType?: string,
    processType?: string,
    gdNumber?: string
  ) => {
    setPageLoader(true);
    try {
      let body = {
        gatePassId: gatePassId,
        vehicleNumber: vehicleNumber,
        messageType: messageType,
        processType: processType,
      };

      if (processType == processTypeName.import) {
        body = { ...body, ...{ billNumber: gdNumber } };
      }
      if (processType == processTypeName.export) {
        body = { ...body, ...{ gdNumber: gdNumber } };
      }

      const results = await fetchWrapper.post("get_weboc_file_detail", body);
      const obj = results?.value?.data[0];
      const transformedColumns = [];
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          let column = {
            accessorKey: key,
            header: transformKey(key),
            flex: 1,
          };
          if (key == "performed") {
            column = {
              ...column,
              ...{
                accessorFn: function customCell(row: any) {
                  return `${
                    row.performed ? moment(row.performed).format("DD-MM-YYYY hh:mm:ss A") : ""
                  }`;
                },
              },
            };
          }
          if (key == "gateIn") {
            column = {
              ...column,
              ...{
                accessorFn: function customCell(row: any) {
                  return `${row.gateIn ? moment(row.gateIn).format("DD-MM-YYYY hh:mm:ss A") : ""}`;
                },
              },
            };
          }
          if (key == "gateOut_time") {
            column = {
              ...column,
              ...{
                accessorFn: function customCell(row: any) {
                  return `${
                    row.gateOut_time ? moment(row.gateOut_time).format("DD-MM-YYYY hh:mm:ss A") : ""
                  }`;
                },
              },
            };
          }
          transformedColumns.push(column);
        }
      }

      setFileProcessTypeColumns(transformedColumns);
      setFileProcessTypeList(results?.value?.data);
      setPageLoader(false);
      setIsOpenFileDailog(true);
    } catch (error) {
      setPageLoader(false);
    }
  };

  const weBOCFileGenerateHandler = async (
    gatePassId?: number,
    vehicleNumber?: string,
    messageType?: string,
    processType?: string,
    gdNumber?: string
  ) => {
    setPageLoader(true);
    try {
      let body = {
        gatePassId: gatePassId,
        vehicleNumber: vehicleNumber,
        messageType: messageType,
        processType: processType,
      };

      if (processType == processTypeName.import) {
        body = { ...body, ...{ billNumber: gdNumber } };
      }
      if (processType == processTypeName.export) {
        body = { ...body, ...{ gdNumber: gdNumber } };
      }

      await fetchWrapper.put("regenerate_weboc_message", body);
      setPageLoader(false);
    } catch (error) {
      setPageLoader(false);
    }
  };

  const resetSearch = () => {
    formik.resetForm();
    setGridRecord([]);
  };

  function handleValueChange(e: any) {
    formik.handleChange(e);
  }

  useEffect(() => {
    if (gridRecord.length && formik.values.processType == processTypeName.import) {
      importTransformColumn(
        impDisplayColumns,
        getWeBOCFileDetails,
        weBOCFileGenerateHandler,
        formik,
        setImpDisplayColumns
      );
      return;
    }
    if (gridRecord.length && formik.values.processType == processTypeName.export) {
      exportTransformColumn(
        expDisplayColumns,
        getWeBOCFileDetails,
        weBOCFileGenerateHandler,
        formik,
        setExpDisplayColumns
      );
      return;
    }
  }, [gridRecord]);

  useEffect(() => {
    getProcessTypeList();
  }, []);

  return (
    <>
      <TabHeader module={moduleName} heading={pageHeading} />
      <Backdrop className={classes.backdrop} open={pageLoader}>
        <CommonLoader />
      </Backdrop>

      <Box m={2} />
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={9}>
              <Box mt={1} mb={2} display="flex">
                <Typography className={classes.pageHeading}> {pageHeading} </Typography>
                <Tooltip title="File Details">
                  <Button
                    style={{
                      minWidth: "30px",
                      padding: "4px",
                      color: "#0d99ff",
                    }}
                    onClick={() => setIsOpenInfoDailog(true)}
                  >
                    <Info />
                  </Button>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12} md={3} direction="row">
              <Box textAlign="right" mt={1} mb={1}>
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetSearch()}>
                  Reset
                </CommonButton>
                <CommonButton
                  cssClass="customSubmitBtn"
                  isGreen
                  onClick={() => formik.submitForm()}
                >
                  Search
                </CommonButton>
              </Box>
            </Grid>
          </Grid>

          <Grid spacing={1} container direction="row">
            <Grid item xs={12} md={3}>
              <Box>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  error={formik.touched.processType && Boolean(formik.errors.processType)}
                  helperText={formik.touched.processType && formik.errors.processType}
                  label="WeBOC Process"
                  placeholder="Select WeBOC Process"
                  name="processType"
                  optionsType={webokProcess}
                  required
                  getOptionLabel={(option: any) => option.name || ""}
                  value={
                    webokProcess.find((st: any) => st.name === formik.values.processType) || null
                  }
                  onChange={(option: any) => {
                    if (option) {
                      resetSearch();
                      formik.setFieldValue("processType", option.name);
                      handleValueChange({ target: { value: option.id } });
                    }
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box>
                <TextField
                  required
                  size="small"
                  fullWidth
                  className="customTextField"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label={getLabel(formik.values.processType)}
                  name="gdNumber"
                  id="gdNumber"
                  placeholder={`Enter ${getLabel(formik.values.processType)}`}
                  disabled={false}
                  value={formik.values.gdNumber}
                  onChange={handleValueChange}
                  error={formik.touched.gdNumber && Boolean(formik.errors.gdNumber)}
                  helperText={formik.touched.gdNumber && formik.errors.gdNumber}
                />
              </Box>
            </Grid>
          </Grid>
          <CommonMaterialGrid
            defaultPerPageRecord={dppr}
            height={gridMaxHeight}
            loader={loader}
            rows={gridRecord || []}
            columns={getColumn(formik?.values?.processType, impDisplayColumns, expDisplayColumns)}
          />
        </CardContent>
      </Card>

      <CommonDialog
        isOpen={isOpenInfoDailog}
        handleClose={() => setIsOpenInfoDailog(false)}
        maxWidth="md"
        heading={
          <Box display="flex" alignItems="center">
            <Typography className={classes.heading}>WeBOC Messages Flow</Typography>
          </Box>
        }
      >
        <CommonMaterialGrid
          rows={getInfoRows(formik?.values?.processType)}
          columns={getInfoColumns(formik?.values?.processType)}
          loader={loader}
          defaultPerPageRecord={15}
          height={300}
          hideFooter={false}
        />
      </CommonDialog>

      <CommonDialog
        isOpen={isOpenFileDailog}
        handleClose={() => setIsOpenFileDailog(false)}
        maxWidth="lg"
        heading={
          <Box display="flex" alignItems="center">
            <Typography className={classes.heading}>WeBOC Message Detail</Typography>
          </Box>
        }
      >
        <CommonMaterialGrid
          rows={fileProcessTypeList || []}
          columns={fileProcessTypeColumns}
          loader={loader}
          defaultPerPageRecord={10}
          height={300}
        />
      </CommonDialog>
    </>
  );
}
